import { Search } from 'lucide-react'
import { useSearchParams } from 'react-router'

import { Input } from '@/components/ui/input'
import { useDebouncedInput } from '@/hooks/useDebouncedInput'
import { useStaffUsers } from '@/models/user'
import {
  MoreFiltersDialog,
  type MoreFiltersState,
  RepSelect,
  SourceSelect,
  StatusSelect,
} from './LeadFilters'

// The same search + filter controls used on the table view, driven entirely by
// URL params, so the table and the pipeline board share one filtering surface.
// `showStatus` is off on the board, where the columns already are the statuses.
export function LeadFilterBar({ showStatus = true }: { showStatus?: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const { staffUsers } = useStaffUsers()

  const search = searchParams.get('search') || ''
  const statusFilter = searchParams.get('status') || ''
  const expiringDays = searchParams.get('expiring') || ''
  const notExpiringDays = searchParams.get('not_expiring') || ''
  const assignedRep = searchParams.get('rep') || ''
  const source = searchParams.get('source') || ''
  const stateFilter = searchParams.get('state') || ''
  const minScore = searchParams.get('min_score') || ''
  const safetyRating = searchParams.get('safety_rating') || ''
  const buyerType = searchParams.get('buyer_type') || ''
  const minUnits = searchParams.get('min_units') || ''
  const maxUnits = searchParams.get('max_units') || ''
  const unreadOnly = searchParams.get('unread') === '1'
  const followupOnly = searchParams.get('followup') === '1'
  const tagsFilter = searchParams.get('tags') || ''
  const excludeTagsFilter = searchParams.get('exclude_tags') || ''
  const eligibilityFilter = searchParams.get('eligibility') || ''

  function updateParams(updates: Record<string, string | undefined>) {
    const params: Record<string, string> = {}
    for (const [key, value] of searchParams.entries()) params[key] = value
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === undefined) delete params[key]
      else params[key] = value
    })
    setSearchParams(params)
  }

  const { inputValue, setInputValue } = useDebouncedInput(search, (value) => {
    updateParams({ search: value, page: undefined })
  })

  const moreFilters: MoreFiltersState = {
    statusFilter,
    expiringDays,
    notExpiringDays,
    assignedRep,
    stateFilter,
    minScore,
    safetyRating,
    buyerType,
    minUnits,
    maxUnits,
    unreadOnly,
    followupOnly,
    tagIds: tagsFilter,
    excludeTagIds: excludeTagsFilter,
    eligibility: eligibilityFilter,
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative max-w-sm flex-1">
        <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          placeholder="Search by name, DOT, or contact..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="border-neutral-300 pl-9 text-sm"
        />
      </div>
      {showStatus && (
        <StatusSelect
          value={statusFilter}
          onChange={(v) => updateParams({ status: v, page: undefined })}
          className="w-48"
        />
      )}
      <SourceSelect
        value={source}
        onChange={(v) => updateParams({ source: v, page: undefined })}
        className="w-36"
      />
      <RepSelect
        value={assignedRep}
        staffUsers={staffUsers}
        onChange={(v) => updateParams({ rep: v, page: undefined })}
        className="w-36"
      />
      <MoreFiltersDialog
        filters={moreFilters}
        staffUsers={staffUsers}
        updateParams={updateParams}
        showStatus={showStatus}
      />
    </div>
  )
}
